import { useEffect, useState } from 'react';

import { Col, Row, Skeleton } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  doGetAccountBalance,
  accountsSetValues,
  getTransfers,
  doRemoveRedirectToSummaryAfterAccountSubmit,
  doGetPositionList,
} from 'src/actions';
import { FilterBarConstant, NavigatePathConstant } from 'src/constants';
import {
  useAccountBalance,
  usePositions,
  useFindAccountStatus,
  useSubmittedAccountSelector,
  useTransfers,
} from 'src/hooks';
import { OfferOrderStatusLabel, Transfer, TransferStatusLabel, Position, AccountBalanceSummaryView } from 'src/models';
import { ColorConstant, Spacing } from 'src/styles';

import { AccountSummary } from './AccountSummary';
import { NonTradedHoldingsTable } from './NonTradedHoldingsTable';
import { SecurityHoldingsTable } from './SecurityHoldingsTable';

export const SummaryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [balanceSummaryView, setBalanceSummaryView] = useState<AccountBalanceSummaryView>({});
  const [pendingTransfersTotal, setPendingTransfersTotal] = useState<number | typeof NaN>(0);
  const [nonTradedPositionsTotal, setNonTradedPositionsTotal] = useState<number | typeof NaN>(0);
  const [tradedPositionsTotal, setTradedPositionsTotal] = useState<number | typeof NaN>(0);

  const totalValue = useSelector((state: any) => state.accountDetails?.accountValue?.data?.totalValue);

  const { account } = useSubmittedAccountSelector();
  const {
    cashBalance,
    cashAvailable,
    pendingOfferOrdersTotal,
    isAccountBalanceLoading,
    hasAccountBalanceError,
    accountBalanceError,
  } = useAccountBalance();
  const {
    tradablePositionList,
    nonTradablePositionList,
    isPositionListLoading,
    hasPositionListError,
    positionListError,
  } = usePositions({
    dependency: [account.accountId],
  });
  const { transferList, isTransferListLoading, hasTransferListError, transferListError } = useTransfers({
    dependency: [account.accountId],
  });
  useFindAccountStatus({ dependency: [account.accountId] });

  const findPendingTransfersTotal = () => {
    return transferList
      .filter((transfer: Transfer) => transfer.status.isProcessing || transfer.status.isPending)
      .reduce((total, transfer) => total + (transfer.direction.isDeposit ? transfer.amount : -transfer.amount), 0);
  };

  const findTradedPositionsTotal = () => {
    return tradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const findNonTradedPositionsTotal = () => {
    return nonTradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const refreshPage = () => {
    dispatch(doGetAccountBalance(account.accountId));
    dispatch(doGetPositionList({ params: { accountId: account.accountId } }));
    dispatch(getTransfers(account.accountId));
  };

  useEffect(() => {
    setPendingTransfersTotal(findPendingTransfersTotal());
  }, [transferList]);

  useEffect(() => {
    setTradedPositionsTotal(findTradedPositionsTotal());
  }, [tradablePositionList]);

  useEffect(() => {
    setNonTradedPositionsTotal(findNonTradedPositionsTotal());
  }, [nonTradablePositionList]);

  useEffect(() => {
    const accountValue = cashBalance + tradedPositionsTotal + nonTradedPositionsTotal;

    dispatch(
      accountsSetValues({
        totalValue: accountValue,
        pendingCashTransactions: pendingTransfersTotal,
        cashAvailable,
      }),
    );
  }, [
    account.accountId,
    cashBalance,
    tradedPositionsTotal,
    nonTradedPositionsTotal,
    pendingTransfersTotal,
    cashAvailable,
  ]);

  useEffect(() => {
    setBalanceSummaryView({
      cashAvailable: {
        value: cashAvailable,
        hasError: hasAccountBalanceError,
        error: accountBalanceError,
        color: ColorConstant.GREEN7,
      },
      pendingTransations: {
        value: pendingTransfersTotal,
        hasError: hasTransferListError,
        error: transferListError,
        onClick: () =>
          navigate(
            `../${NavigatePathConstant.HISTORY}?show=${JSON.stringify([
              FilterBarConstant.ShowFilterCategory.DepositTransfer,
              FilterBarConstant.ShowFilterCategory.WithdrawalTransfer,
            ])}&filterBy=${JSON.stringify([
              `${TransferStatusLabel.PENDING}_${FilterBarConstant.ByFilterCategory.MoneyTransfer}`,
              `${TransferStatusLabel.PROCESSING}_${FilterBarConstant.ByFilterCategory.MoneyTransfer}`,
            ])}`,
          ),
        color: ColorConstant.ORANGE7,
      },
      pendingNonTradedHoldings: {
        value: pendingOfferOrdersTotal,
        hasError: hasAccountBalanceError,
        error: accountBalanceError,
        onClick: () =>
          navigate(
            `../${NavigatePathConstant.HISTORY}?show=${JSON.stringify([
              FilterBarConstant.ShowFilterCategory.OfferOrder,
            ])}&filterBy=${JSON.stringify([
              `${OfferOrderStatusLabel.PendingFunds}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.PendingOfferClose}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.PendingAction}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.PendingFirmCancellation}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.Approved}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
            ])}`,
          ),
        color: ColorConstant.RED7,
      },
      securities: {
        value: tradedPositionsTotal,
        hasError: hasPositionListError,
        error: positionListError,
        color: ColorConstant.BRAND7,
      },
      nonTradedHoldings: {
        value: nonTradedPositionsTotal,
        hasError: hasPositionListError,
        error: positionListError,
        color: ColorConstant.BRAND9,
      },
    });
  }, [
    cashAvailable,
    pendingTransfersTotal,
    pendingOfferOrdersTotal,
    tradedPositionsTotal,
    nonTradedPositionsTotal,
    hasAccountBalanceError,
    hasTransferListError,
    hasPositionListError,
  ]);

  useEffect(() => {
    dispatch(doRemoveRedirectToSummaryAfterAccountSubmit());
  }, [dispatch]);

  return (
    <Skeleton loading={isPositionListLoading || isTransferListLoading || isAccountBalanceLoading}>
      <Row>
        <Col span={24}>
          <AccountSummary
            title='Summary'
            isLoading={isPositionListLoading || isTransferListLoading || isAccountBalanceLoading}
            accountValue={totalValue}
            balanceSummary={balanceSummaryView}
            onRefresh={refreshPage}
          />
        </Col>
        <Col span={24} id='securities-section' className={Spacing.my16}>
          <SecurityHoldingsTable />
        </Col>
        <Col span={24} id='non-trade-holdings-section' className={Spacing.my16}>
          <NonTradedHoldingsTable />
        </Col>
      </Row>
    </Skeleton>
  );
};
